import { useEffect, useState } from "react";
import { FilterInput, type FilterInputOption } from "../../components/FilterInput";

type ChoiceOption = FilterInputOption;

export function KpiTile({ label, value, tone }: { label: string; value: string; tone: "good" | "bad" | "warn" }) {
  return (
    <div className={`kpi-tile ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function LedgerRows({ rows }: { rows: Array<[string, string]> }) {
  return (
    <dl className="ledger-rows">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function LedgerMeter({ label, value, max }: { label: string; value: number; max: number }) {
  const ratio = ledgerMeterRatio(value, max);
  return (
    <div className="ledger-meter">
      <span>{label}</span>
      <div style={{ ["--meter" as string]: `${ratio * 100}%` }} />
      <em>{formatKg(value)} / {formatKg(max)}</em>
    </div>
  );
}

function ledgerMeterRatio(value: number, max: number): number {
  const protectedMax = Math.max(1, max);
  const rawRatio = value / protectedMax;
  const cappedRatio = Math.min(1, rawRatio);
  return Math.max(0, cappedRatio);
}

export function SearchableChoice(props: {
  id: string;
  label?: string;
  value: string;
  options: ChoiceOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const selectedLabel = props.options.find((option) => option.value === props.value)?.label ?? "";
  const [query, setQuery] = useState(selectedLabel);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  const applyQuery = (nextQuery: string) => {
    if (props.disabled) return;
    setQuery(nextQuery);
    const normalized = normalizeChoiceText(nextQuery);
    if (!normalized) return;
    const exactMatch = props.options.find((option) => normalizeChoiceText(option.label) === normalized || normalizeChoiceText(option.value) === normalized);
    if (exactMatch) {
      props.onChange(exactMatch.value);
      return;
    }
    const matches = props.options.filter((option) => searchableChoiceText(option).includes(normalized));
    if (matches.length === 1) {
      props.onChange(matches[0].value);
    }
  };

  return (
    <FilterInput
      id={props.id}
      label={props.label}
      query={query}
      options={props.options}
      onQueryChange={applyQuery}
      onSelect={(option) => {
        if (props.disabled) return;
        setQuery(option.label);
        props.onChange(option.value);
      }}
      onInputBlur={() => {
        if (props.disabled) return;
        if (!selectedLabel) return;
        const normalized = normalizeChoiceText(query);
        if (!normalized || !searchableChoiceText({ value: props.value, label: selectedLabel }).includes(normalized)) {
          setQuery(selectedLabel);
        }
      }}
      placeholder={props.placeholder ?? "输入搜索"}
      disabled={props.disabled}
    />
  );
}

export function formatKg(value: number | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "--";
  }
  const rounded = Math.round(value);
  if (Math.abs(rounded) >= 1000) {
    return `${(rounded / 1000).toFixed(1)}t`;
  }
  return `${rounded}kg`;
}

function normalizeChoiceText(value: string): string {
  return value.trim().toUpperCase();
}

function searchableChoiceText(option: ChoiceOption): string {
  return normalizeChoiceText([option.value, option.label, option.detail].filter(Boolean).join(" "));
}
